import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComments } from "@fortawesome/free-solid-svg-icons";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CHATBOT_ENABLED } from "../../config/env";

const DEFAULT_API_ROOT =
  process.env.REACT_APP_CHATBOT_API_ROOT || "https://np3v0i9mm1.execute-api.eu-west-1.amazonaws.com/chat";

const isChatbotEnabled = CHATBOT_ENABLED === "true";

const DEFAULT_WELCOME_MESSAGE =
  "Ask me anything related to this project.";

const EMPTY_CONVERSATION = {
  context: "",
  messages: [],
  isLoading: false,
  error: ""
};

const createRequestUrl = (apiRoot, project) => {
  const safeProject = encodeURIComponent(String(project || "").trim() || "chat");
  return `${String(apiRoot || DEFAULT_API_ROOT).replace(/\/+$/, "")}/${safeProject}`;
};

const getProjectKey = (project) => String(project || "").trim() || "chat";

const formatMessageContent = (content) => {
  const markdownContent =
    typeof content === "string" && content.trim() ? content : "No response received.";

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ node, ...props }) => <p className="chatbot__paragraph" {...props} />,
        ul: ({ node, ...props }) => <ul className="chatbot__list" {...props} />,
        ol: ({ node, ...props }) => <ol className="chatbot__list" {...props} />,
        a: ({ node, children, ...props }) => (
          <a target="_blank" rel="noreferrer noopener" {...props}>
            {children}
          </a>
        )
      }}
    >
      {markdownContent}
    </ReactMarkdown>
  );
};

const MessageBubble = ({ role, content }) => {
  const isUser = role === "user";

  return (
    <div
      className={`chatbot__message-row ${
        isUser ? "chatbot__message-row--user" : "chatbot__message-row--assistant"
      }`}
    >
      <div
        className={`chatbot__message ${
          isUser ? "chatbot__message--user" : "chatbot__message--assistant"
        }`}
      >
        <div className="chatbot__message-label">{isUser ? "You" : "Assistant"}</div>
        <div className="chatbot__message-content">{formatMessageContent(content)}</div>
      </div>
    </div>
  );
};

const Chatbot = ({
  project,
  title = "Chatbot",
  placeholder = "Type a message...",
  className = "",
  apiRoot = DEFAULT_API_ROOT
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [conversations, setConversations] = useState({});

  const panelRef = useRef(null);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);

  const projectKey = useMemo(() => getProjectKey(project), [project]);
  const apiUrl = useMemo(() => createRequestUrl(apiRoot, project), [apiRoot, project]);
  const currentConversation = conversations[projectKey] || EMPTY_CONVERSATION;
  const { context, messages, isLoading, error } = currentConversation;

  const updateConversation = (targetProjectKey, updater) => {
    setConversations((prev) => {
      const current = prev[targetProjectKey] || EMPTY_CONVERSATION;
      return {
        ...prev,
        [targetProjectKey]: updater(current)
      };
    });
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleDocumentClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isLoading, isOpen]);

  useEffect(() => {
    setInput("");
  }, [projectKey]);

  if (!isChatbotEnabled) {
    return null;
  }

  const toggleOpen = () => {
    setIsOpen((current) => !current);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const message = input.trim();
    if (!message || isLoading) {
      return;
    }

    const activeProjectKey = projectKey;
    const activeApiUrl = apiUrl;

    setInput("");
    updateConversation(activeProjectKey, (prev) => ({
      ...prev,
      error: "",
      isLoading: true,
      messages: [...prev.messages, { role: "user", content: message }]
    }));

    try {
      const response = await fetch(activeApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          context,
          message
        })
      });

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      const data = await response.json();
      const botResponse =
        typeof data?.response === "string" && data.response.trim()
          ? data.response
          : "No response received.";
      const updatedContext = typeof data?.context === "string" ? data.context : context;

      updateConversation(activeProjectKey, (prev) => ({
        ...prev,
        context: updatedContext,
        messages: [...prev.messages, { role: "assistant", content: botResponse }]
      }));
    } catch (err) {
      const messageText = err instanceof Error ? err.message : "Something went wrong.";
      updateConversation(activeProjectKey, (prev) => ({
        ...prev,
        error: messageText,
        messages: [
          ...prev.messages,
          {
            role: "assistant",
            content: "I could not reach the chatbot endpoint."
          }
        ]
      }));
    } finally {
      updateConversation(activeProjectKey, (prev) => ({
        ...prev,
        isLoading: false
      }));
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  return (
    <div className={`chatbot ${className}`.trim()} ref={panelRef}>
      {isOpen ? (
        <section className="chatbot__panel" aria-label={title}>
          <header className="chatbot__header">
            <div>
              <h2 className="chatbot__title">{title}</h2>
              <p className="chatbot__subtitle">Project: {project || "chat"}</p>
            </div>
            <button
              type="button"
              className="chatbot__close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chatbot"
            >
              X
            </button>
          </header>

          <div className="chatbot__messages" ref={messagesRef}>
            {messages.length ? (
              messages.map((item, index) => (
                <MessageBubble
                  key={`${item.role}-${index}-${item.content.slice(0, 20)}`}
                  role={item.role}
                  content={item.content}
                />
              ))
            ) : (
              <div className="chatbot__empty-state">
                <p>{DEFAULT_WELCOME_MESSAGE}</p>
              </div>
            )}

            {isLoading ? (
              <div className="chatbot__message-row chatbot__message-row--assistant">
                <div className="chatbot__message chatbot__message--assistant chatbot__typing">
                  <div className="chatbot__message-label">Assistant</div>
                  <div className="chatbot__typing-dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {error ? <div className="chatbot__error">{error}</div> : null}

          <form className="chatbot__form" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              rows="1"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={isLoading}
              className="chatbot__input"
            />
            <button type="submit" disabled={isLoading} className="chatbot__send">
              {isLoading ? "Thinking..." : "Send"}
            </button>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        className="chatbot__trigger"
        onClick={toggleOpen}
        aria-label={isOpen ? "Close chatbot" : "Open chatbot"}
      >
        <span className="chatbot__trigger-icon">{isOpen ? "X" : <FontAwesomeIcon icon={faComments} />}</span>
      </button>
    </div>
  );
};

export default Chatbot;
